/**
 * «Названия тех.операций» — тенант-справочник по эталону культур (AK-564).
 *
 * Founder на staging 08.08: «по справочнику культур и тех.операций — истории
 * там одни и те же, а UX какой-то не одинаковый». Так и было: экран собирался
 * 05–07.08 (#708, #714), а культуры переделали по определяющему макету 08.08
 * (#724) уже после трёх раундов UAT. Здесь экран приведён к той же анатомии,
 * что `TenantCatalogListPage`:
 *
 *  · шапка с одним предложением «зачем список и кто его видит»;
 *  · тихая строка про неразобранное вместо синей плашки заявок;
 *  · ряд чипов (класс работы + «Без привязки» + «Архив») и поиск справа —
 *    один и тот же helper нормализации ё=е, что у культур;
 *  · вес записи колонкой «Операций» — за всё время, как «Посевов»
 *    (решение founder 2 от 10.08); числа без ссылок: сезон и хозяйство не
 *    заданы, ссылка вела бы в двусмысленное место;
 *  · клик по строке открывает карточку. Трёх иконок в строке нет: заявка в
 *    системный каталог живёт в карточке, а очередь заявок — на вкладке
 *    «Интеграции и импорт» (одна работа — одна дверь).
 *
 * Доменные различия сохранены осознанно: у операции нет цвета (она не рисуется
 * на карте), зато есть класс работы и свои поля.
 */
import { useMemo, useState } from 'react';
import { CircleAlert, GitMerge, Plus, Search } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import { formatRuDate } from '@/lib/dateFmt';
import { useCan } from '@/lib/auth/usePermissions';
import { SEARCH_PLACEHOLDER, matchesSearch } from '@/lib/reference/normalizeSearch';
import { useReviewSummary } from '@/lib/reference/catalogReview';
import {
  cotVisibilityLabel,
  opClassChipLabel,
  operationsCountLabel,
  ownFieldsLabel,
  referenceOpClassLabel,
  useArchiveCompanyOpType,
  useCompanyOpTypes,
  type CompanyOpType,
} from '@/lib/reference/companyOperationTypes';
import { CompanyOpTypeDialog } from './CompanyOpTypeDialog';
import { CompanyOpTypeSheet } from './CompanyOpTypeSheet';
import { OpTypeMergeSheet } from './OpTypeMergeSheet';

/** Чип-фильтр: класс работы + отдельная куча «привязки нет». */
type ClassFilter = 'all' | 'field' | 'logistics' | 'warehouse' | 'unlinked';

/**
 * К какой куче относится название. Решение founder 1 (10.08): у названия без
 * привязки класса работы нет, и без своего чипа оно проваливалось бы мимо всех
 * фильтров — заводим «Без привязки», он же тихо показывает админу, что в
 * справочнике есть недоделанное.
 */
function rowClass(t: CompanyOpType): Exclude<ClassFilter, 'all'> {
  if (!t.ref_id) return 'unlinked';
  switch (t.op_class) {
    case 'field':
    case 'logistics':
    case 'warehouse':
      return t.op_class;
    default:
      return 'unlinked';
  }
}

export function CompanyOpTypesPage() {
  // Ресурс `operations` — тот же, которым бэк гейтит сами операции и works.
  // (Долг: у справочника нет своего RBAC-ресурса — отдельным тикетом.)
  const canWrite = useCan('operations', 'u');
  const canCreate = useCan('operations', 'c');
  const canDelete = useCan('operations', 'd');
  const canReview = useCan('catalog_review', 'r');

  const [archivedFilter, setArchivedFilter] = useState(false);
  const [classFilter, setClassFilter] = useState<ClassFilter>('all');
  const [search, setSearch] = useState('');
  const [addOpen, setAddOpen] = useState(false);
  const [openRecord, setOpenRecord] = useState<CompanyOpType | null>(null);
  // Выбор для слияния храним по id: при фильтрации индексы плывут, id — нет.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mergeOpen, setMergeOpen] = useState(false);

  const query = useCompanyOpTypes({ includeArchived: archivedFilter });
  const archive = useArchiveCompanyOpType();
  const summary = useReviewSummary('operation_type', canReview);
  const pendingReview =
    (summary.data?.pending_in_batches ?? 0) + (summary.data?.outside_batches ?? 0);

  const all = query.data ?? [];
  // Архив — ФИЛЬТР над тем же списком, а не отдельный экран (эталон, С-5).
  const records = useMemo(
    () => all.filter((t) => Boolean(t.archived_at) === archivedFilter),
    [all, archivedFilter],
  );

  const counts = useMemo(() => {
    const acc: Record<Exclude<ClassFilter, 'all'>, number> = {
      field: 0,
      logistics: 0,
      warehouse: 0,
      unlinked: 0,
    };
    for (const t of records) acc[rowClass(t)] += 1;
    return acc;
  }, [records]);

  const rows = useMemo(
    () =>
      records
        .filter((t) => (classFilter === 'all' ? true : rowClass(t) === classFilter))
        .filter((t) => matchesSearch(t.local_name, search))
        .sort((a, b) => a.local_name.localeCompare(b.local_name, 'ru')),
    [records, classFilter, search],
  );

  // Сливать можно только активные строки: архив — не участник склейки.
  const selectedRows = useMemo(
    () => all.filter((t) => selectedIds.has(t.id) && !t.archived_at),
    [all, selectedIds],
  );

  // Карточка держит свежую версию из кэша: после добавления поля список
  // перезапрашивается, и открытая панель обязана показать новое состояние.
  const openItem = openRecord
    ? (all.find((t) => t.id === openRecord.id) ?? openRecord)
    : null;

  function toggleSelected(id: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  const chips: Array<{ key: ClassFilter; label: string; n: number }> = [
    { key: 'all', label: 'Все', n: records.length },
    { key: 'field', label: opClassChipLabel('field') as string, n: counts.field },
    {
      key: 'logistics',
      label: opClassChipLabel('logistics') as string,
      n: counts.logistics,
    },
    {
      key: 'warehouse',
      label: opClassChipLabel('warehouse') as string,
      n: counts.warehouse,
    },
    { key: 'unlinked', label: 'Без привязки', n: counts.unlinked },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Названия тех.операций
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Как работы называются у вас. Именно этот список видит учётчик, когда
            заводит операцию по полю, — с поправкой на то, где название видно.
          </p>
        </div>
        {canCreate && (
          <Button size="sm" className="shrink-0" onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Добавить название
          </Button>
        )}
      </div>

      {/* Тихая строка вместо синей плашки заявок: неразобранное не мешает
          работать со списком, а заявки живут там, где их разбирают. */}
      {pendingReview > 0 && (
        <p className="text-sm text-muted-foreground">
          Ещё {pendingReview} названий ждут разбора — они на вкладке «Интеграции
          и импорт» и в список пока не входят.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {chips
          .filter((c) => c.key === 'all' || c.n > 0)
          .map((c) => (
            <button
              key={c.key}
              type="button"
              aria-pressed={classFilter === c.key}
              onClick={() => setClassFilter(c.key)}
              className={cn(
                'rounded-full border px-3 py-1 text-sm transition-colors',
                classFilter === c.key
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-card text-foreground hover:bg-accent',
              )}
            >
              {c.label} · {c.n}
            </button>
          ))}
        <button
          type="button"
          aria-pressed={archivedFilter}
          onClick={() => {
            setArchivedFilter((v) => !v);
            setSelectedIds(new Set()); // набор строк меняется — выбор сбрасываем
          }}
          className={cn(
            'rounded-full border px-3 py-1 text-sm transition-colors',
            archivedFilter
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border bg-card text-muted-foreground hover:bg-accent',
          )}
        >
          Архив
        </button>
        <div className="relative ml-auto min-w-56">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={SEARCH_PLACEHOLDER}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      {query.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-md" />
          ))}
        </div>
      ) : query.isError ? (
        <Alert variant="destructive">
          <CircleAlert className="h-4 w-4" />
          <AlertTitle>Не удалось загрузить справочник</AlertTitle>
          <AlertDescription>
            <Button variant="outline" size="sm" onClick={() => query.refetch()}>
              Повторить
            </Button>
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-sm font-medium text-foreground">
            {records.length === 0
              ? archivedFilter
                ? 'В архиве пусто'
                : 'Справочник пока пуст'
              : 'Ничего не нашли'}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {records.length === 0
              ? 'Добавьте название вручную или разберите то, что пришло, во вкладке «Интеграции и импорт». Пока справочник пуст, учётчик при заведении операции видит подсказку «обратитесь к админу холдинга».'
              : 'Попробуйте другое написание или снимите фильтр по классу работы.'}
          </p>
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                {canWrite && !archivedFilter && (
                  <TableHead className="w-8" aria-label="Выбрать" />
                )}
                <TableHead className="w-[32%]">Название</TableHead>
                <TableHead className="w-[16%]">Класс работы</TableHead>
                {archivedFilter ? (
                  <TableHead className="w-[22%]">В архиве с</TableHead>
                ) : (
                  <>
                    <TableHead className="w-[22%]">В системе это</TableHead>
                    <TableHead className="w-[16%]">Видно</TableHead>
                  </>
                )}
                <TableHead className="w-[14%]">Операций</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((t) => {
                const ownFields = ownFieldsLabel(t);
                return (
                  <TableRow
                    key={t.id}
                    tabIndex={0}
                    role="button"
                    aria-label={`Открыть «${t.local_name}»`}
                    className="group cursor-pointer"
                    onClick={() => setOpenRecord(t)}
                    onKeyDown={(e) => {
                      // Событие с чекбокса (Radix рисует его кнопкой) всплывает
                      // сюда, и `preventDefault` гасил бы Space НА ЧЕКБОКСЕ:
                      // мышью выбрать можно, с клавиатуры — нет. Реагируем
                      // только на клавиши, нажатые в самой строке.
                      if (e.target !== e.currentTarget) return;
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenRecord(t);
                      }
                    }}
                  >
                    {canWrite && !archivedFilter && (
                      // Чекбокс появляется на наведении/фокусе и остаётся, пока
                      // строка выбрана: постоянные чекбоксы во всех строках —
                      // визуальный шум ради разовой операции слияния.
                      <TableCell
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Checkbox
                          className={cn(
                            'transition-opacity',
                            selectedIds.has(t.id)
                              ? 'opacity-100'
                              : 'opacity-0 focus-visible:opacity-100 group-hover:opacity-100',
                          )}
                          checked={selectedIds.has(t.id)}
                          onCheckedChange={(v) => toggleSelected(t.id, Boolean(v))}
                          aria-label={`Выбрать «${t.local_name}» для слияния`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-medium text-foreground">
                      <span className="min-w-0 truncate">{t.local_name}</span>
                      {ownFields && (
                        <div className="text-xs text-muted-foreground">{ownFields}</div>
                      )}
                    </TableCell>
                    <TableCell>
                      {referenceOpClassLabel(t.op_class) ? (
                        <Badge variant="outline">
                          {referenceOpClassLabel(t.op_class)}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {archivedFilter ? (
                      <TableCell className="text-sm text-muted-foreground">
                        {t.archived_at ? formatRuDate(t.archived_at) : '—'}
                      </TableCell>
                    ) : (
                      <>
                        <TableCell className="text-sm text-muted-foreground">
                          {t.ref_name_ru ?? 'своя запись'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {cotVisibilityLabel(t)}
                        </TableCell>
                      </>
                    )}
                    <TableCell className="text-sm tabular-nums text-muted-foreground">
                      {operationsCountLabel(t.usage_count ?? 0)}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Нижняя панель выбора — вход в слияние. Кнопки «Слить» в шапке нет:
          пока никого не выбрали, ей там нечего делать. */}
      {canWrite && selectedRows.length >= 2 && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-card p-4">
          <span className="text-sm text-muted-foreground">
            Выбрано{' '}
            <strong className="text-foreground">{selectedRows.length}</strong>{' '}
            названия — их можно слить в одно.
          </span>
          <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>
              Снять выбор
            </Button>
            <Button size="sm" onClick={() => setMergeOpen(true)}>
              <GitMerge className="mr-2 h-4 w-4" />
              Слить выбранные ({selectedRows.length})
            </Button>
          </div>
        </div>
      )}

      <CompanyOpTypeDialog open={addOpen} onClose={() => setAddOpen(false)} />

      <CompanyOpTypeSheet
        item={openItem}
        open={openRecord !== null}
        canWrite={canWrite}
        canArchive={canDelete}
        onOpenChange={(o) => !o && setOpenRecord(null)}
        /* Рабочий выход из гарда перепривязки: старое название в архив, форма
           сразу открывается на создание нового — вместо «разбирайтесь сами». */
        onArchiveAndCreate={(t) =>
          archive.mutate(t.id, {
            onSuccess: () => {
              toast.success('Название в архиве — заведите новое');
              setOpenRecord(null);
              setAddOpen(true);
            },
            onError: (e) => toast.error(e.message || 'Не удалось заархивировать'),
          })
        }
      />

      <OpTypeMergeSheet
        open={mergeOpen}
        selected={selectedRows}
        onClose={() => setMergeOpen(false)}
        onMerged={() => setSelectedIds(new Set())}
      />
    </div>
  );
}

export default CompanyOpTypesPage;
